import { FunctionComponent, useState } from 'react';
import {
    Box,
    Drawer,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { useGetCashDesksQuery } from '@/api/v2/cashDesk';
import { CashDesk } from '@/models/cashDesk';
import { formatStarjackDate } from '@/utils/time';
import { CashDeskDrawerContent } from '@/pageContents/cashDesks/CashDeskDrawerContent';
import { CashDeskDeleteDialog } from '@/pageContents/cashDesks/CashDeskDeleteDialog';

export const CashDesksContent: FunctionComponent = () => {
    const theme = useTheme();
    const { data, isLoading: cashDesksLoading } = useGetCashDesksQuery();
    const { currentItems, page, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            items: data,
            itemsPerPage: 10,
        });
    const [createCashOpen, setCreateCashOpen] = useState(false);
    const [cashDeskToDelete, setCashDeskToDelete] = useState<{
        toDelete: CashDesk;
        isOpen: boolean;
    }>({ toDelete: null, isOpen: false });

    const closeDrawer = () => {
        setCreateCashOpen(false);
    };

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Kassen
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <LoadingButton
                        onClick={() => {
                            setCreateCashOpen(true);
                        }}
                        variant='contained'
                        loading={false}
                    >
                        + Neue Kasse anlegen
                    </LoadingButton>
                </Box>
                <StarjackTable<CashDesk>
                    count={data?.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Benutzername',
                            itemKey: 'username',
                        },
                        {
                            label: 'Liftoperator Id ',
                            itemKey: 'liftOperatorId',
                        },

                        {
                            label: 'Erstellt am',
                            itemTransform: (item) =>
                                formatStarjackDate(item.createdAt),
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (item) => (
                                <IconButton
                                    onClick={() => {
                                        setCashDeskToDelete({
                                            isOpen: true,
                                            toDelete: item,
                                        });
                                    }}
                                >
                                    <SvgIcon fontSize='small'>
                                        <DeleteIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                        },
                    ]}
                    currentPage={page}
                    itemId={'id'}
                    loading={cashDesksLoading}
                    onPageChange={setNextPage}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                />

                <Drawer
                    anchor='right'
                    open={createCashOpen}
                    onClose={closeDrawer}
                >
                    <CashDeskDrawerContent onClose={closeDrawer} />
                </Drawer>

                <CashDeskDeleteDialog
                    open={cashDeskToDelete.isOpen}
                    onClose={() => {
                        setCashDeskToDelete((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    cashDesk={cashDeskToDelete.toDelete}
                />
            </Stack>
        </>
    );
};
